/**
 * Device Detection Utilities
 * Aligns with Bootstrap 4.6 breakpoints defined in _variables.scss
 * 
 * Breakpoints:
 * - Phone: < 768px (xs, sm collapsed to mobile)
 * - Tablet: 600px - 1199px (custom tablet range + md/lg)
 * - Desktop: 1200px - 1439px (xl)
 * - Large Desktop: >= 1440px (xxl)
 * 
 * Usage:
 * var device = require('./deviceDetection');
 * if (device.isPhone()) { ... }
 * var deviceType = device.getDeviceType(); // 'phone' | 'tablet' | 'desktop' | 'large-desktop'
 */

// Breakpoint constants (aligned with SCSS $grid-breakpoints)
var BREAKPOINTS = {
    PHONE_MAX: 767,
    TABLET_MIN: 600,
    TABLET_MAX: 1199,
    DESKTOP_MIN: 1200,
    LARGE_DESKTOP_MIN: 1440
};

/**
 * Get current device type based on viewport width.
 * @returns {string} Returns one of: 'phone', 'tablet', 'desktop', or 'large-desktop'.
 */
function getDeviceType() {
    var width = window.innerWidth;

    if (width < BREAKPOINTS.PHONE_MAX) {
        return 'phone';
    }
    if (width >= BREAKPOINTS.TABLET_MIN && width <= BREAKPOINTS.TABLET_MAX) {
        return 'tablet';
    }
    if (width >= BREAKPOINTS.DESKTOP_MIN && width < BREAKPOINTS.LARGE_DESKTOP_MIN) {
        return 'desktop';
    }
    return 'large-desktop';
}

/**
 * Check if current device is a phone.
 * @returns {boolean} True if width < tablet minimum.
 */
function isPhone() {
    return window.innerWidth < BREAKPOINTS.TABLET_MIN;
}

/**
 * Check if current device is a tablet.
 * @returns {boolean} True if width is between tablet min and tablet max.
 */
function isTablet() {
    var width = window.innerWidth;
    return width >= BREAKPOINTS.TABLET_MIN && width <= BREAKPOINTS.TABLET_MAX;
}

/**
 * Check if current device is a desktop.
 * @returns {boolean} True if width is between desktop min and large-desktop min.
 */
function isDesktop() {
    var width = window.innerWidth;
    return width >= BREAKPOINTS.DESKTOP_MIN && width < BREAKPOINTS.LARGE_DESKTOP_MIN;
}

/**
 * Check if current device is a large desktop.
 * @returns {boolean} True if width >= large desktop minimum.
 */
function isLargeDesktop() {
    return window.innerWidth >= BREAKPOINTS.LARGE_DESKTOP_MIN;
}

/**
 * Check if current viewport is mobile.
 * @returns {boolean} True if width <= tablet max.
 */
function isMobile() {
    return window.innerWidth <= BREAKPOINTS.TABLET_MAX;
}

/**
 * Get detailed device information.
 * @returns {Object} Returns a details object including type, size, and device flags.
 */
function getDeviceInfo() {
    var width = window.innerWidth;
    var height = window.innerHeight;
    var deviceType = getDeviceType();
    
    return {
        type: deviceType,
        width: width,
        height: height,
        isPhone: isPhone(),
        isTablet: isTablet(),
        isDesktop: isDesktop(),
        isLargeDesktop: isLargeDesktop(),
        isMobile: isMobile(),
        isPortrait: height > width,
        isLandscape: width > height
    };
}

module.exports = {
    getDeviceType: getDeviceType,
    getDeviceInfo: getDeviceInfo,
    isPhone: isPhone,
    isTablet: isTablet,
    isDesktop: isDesktop,
    isLargeDesktop: isLargeDesktop,
    isMobile: isMobile,
    BREAKPOINTS: BREAKPOINTS
};